/**
 * Route identity for the web-only Journey funnels (/journal/<seoSlug>), kept
 * small so App.tsx and the native route fence can import it without pulling
 * the funnel copy (src/config/journeyFunnels.ts) into the main bundle.
 * journeyFunnelConfig.test.ts keeps the names and SEO titles here identical
 * to the copy config.
 */

import { enforceBrandSuffixLimit } from '@/lib/seoTitle';

export const JOURNEY_FUNNEL_SEO_SLUGS = [
  'overthinking',
  'burnout',
  'start-journaling',
  'clarity',
  'self-discovery',
  'healing',
  'relationships',
  'self-esteem',
  'mindfulness',
  'creativity',
] as const;

export type JourneyFunnelSeoSlug = (typeof JOURNEY_FUNNEL_SEO_SLUGS)[number];

/** Journey name and approved <title> per funnel. */
export const JOURNEY_FUNNEL_ROUTE_TITLES: Record<JourneyFunnelSeoSlug, { name: string; seoTitle: string }> = {
  overthinking: {
    name: 'Clear the Noise',
    seoTitle: 'Journaling for Overthinking: Clear the Noise, a 7-Day Journey | Journal Party',
  },
  burnout: {
    name: 'Find Your Pace',
    seoTitle: 'Journaling for Burnout: Find Your Pace, a 7-Day Journey | Journal Party',
  },
  'start-journaling': {
    name: 'Come Back to the Page',
    seoTitle: 'How to Start Journaling (and Keep Going): a 7-Day Journey | Journal Party',
  },
  clarity: {
    name: 'Find Your Way Forward',
    seoTitle: 'Journaling for Clarity: Find Your Way Forward, a 7-Day Journey | Journal Party',
  },
  'self-discovery': {
    name: 'Return to Yourself',
    seoTitle: 'Journaling for Self-Discovery: Return to Yourself, a 7-Day Journey | Journal Party',
  },
  healing: {
    name: 'Let the Hurt Loosen',
    seoTitle: 'Journaling for Healing: Let the Hurt Loosen, a 7-Day Journey | Journal Party',
  },
  relationships: {
    name: 'Closer, Clearer',
    seoTitle: 'Relationship Journaling: Closer, Clearer, a 7-Day Journey | Journal Party',
  },
  'self-esteem': {
    name: 'Trust Your Own Voice',
    seoTitle: 'Journaling for Self-Esteem and Self-Doubt: Trust Your Own Voice | Journal Party',
  },
  mindfulness: {
    name: 'Feel Here Again',
    seoTitle: 'Mindful Journaling: Feel Here Again, a 7-Day Journey | Journal Party',
  },
  creativity: {
    name: 'Make Something Again',
    seoTitle: 'Journaling for Creativity: Make Something Again, a 7-Day Journey | Journal Party',
  },
};

export function isJourneyFunnelSeoSlug(value: string | null | undefined): value is JourneyFunnelSeoSlug {
  return Boolean(value) && (JOURNEY_FUNNEL_SEO_SLUGS as readonly string[]).includes(value as string);
}

export const JOURNEY_FUNNEL_BASE_PATH = '/journal';

export function journeyFunnelPaths(seoSlug: string) {
  const landing = `${JOURNEY_FUNNEL_BASE_PATH}/${seoSlug}`;
  return {
    landing,
    premiumOffer: `${landing}/premium-offer`,
    premiumDownsell: `${landing}/premium-downsell`,
    confirmation: `${landing}/confirmation`,
  };
}

export type JourneyFunnelStep = 'landing' | 'premium-offer' | 'premium-downsell' | 'confirmation';

const JOURNEY_FUNNEL_PATH_PATTERN =
  /^\/journal\/([a-z0-9]+(?:-[a-z0-9]+)*)(?:\/(premium-offer|premium-downsell|confirmation))?\/?$/i;

/**
 * Parses a /journal/<seoSlug>[/<step>] path. Matches any well-formed slug
 * (the registry decides whether an offer exists); returns null for "/journal"
 * itself, which stays the legacy redirect to /home.
 */
export function matchJourneyFunnelPath(
  pathname: string,
): { seoSlug: string; step: JourneyFunnelStep } | null {
  const match = JOURNEY_FUNNEL_PATH_PATTERN.exec(pathname);
  if (!match) return null;
  return {
    seoSlug: match[1].toLowerCase(),
    step: (match[2]?.toLowerCase() as JourneyFunnelStep | undefined) ?? 'landing',
  };
}

/**
 * Browser tab and page_view title for a funnel route. The landing page uses
 * its approved SEO title; the later steps use only the Journey name so no
 * price or offer wording is compiled here. Null for an unknown slug.
 */
export function getJourneyFunnelRouteTitle(pathname: string): string | null {
  const match = matchJourneyFunnelPath(pathname);
  if (!match || !isJourneyFunnelSeoSlug(match.seoSlug)) return null;
  const titles = JOURNEY_FUNNEL_ROUTE_TITLES[match.seoSlug];
  return enforceBrandSuffixLimit(match.step === 'landing' ? titles.seoTitle : `${titles.name} | Journal Party`);
}
